/**
 * TypeScript interfaces for the Form Builder.
 * These types match the backend data model for full compatibility with existing form data.
 */

export interface ControlOption {
  option: string;
  svgIcon?: string;
  svgIconName?: string;
  color?: string;
  enabled?: boolean;
}

/**
 * Widget a conditional_field renders as. This is a WIDGET TYPE, not a reference to
 * another question: the mobile app switches directly on this string and renders no
 * selector at all for any other value (skewb-mobile
 * src/components/skewbWorks/formSubComponents/MainControl.tsx:1365,1383), which is
 * SC-6511. Legacy offers exactly these three
 * (apps/frontend/.../end-user-builder/PropertiesPanel.tsx:592-594), and they are the
 * safe intersection accepted by every mobile renderer.
 */
export const ConditionalType = {
  RADIO: 'radio',
  OPTION: 'option',
  CHECKBOX: 'checkbox',
} as const;
export type ConditionalType = (typeof ConditionalType)[keyof typeof ConditionalType];

export const isConditionalType = (value: unknown): value is ConditionalType =>
  value === ConditionalType.RADIO ||
  value === ConditionalType.OPTION ||
  value === ConditionalType.CHECKBOX;

/**
 * Mobile renders Vertical via TaskFormComponent, which draws every control of the
 * current section on one screen (skewb-mobile TaskFormComponent.tsx:1361) and steps
 * section by section (findNextStep, :1168-1180). Horizontal steps control by control.
 */
export const FormLayout = {
  HORIZONTAL: 'Horizontal',
  VERTICAL: 'Vertical',
} as const;
export type FormLayout = (typeof FormLayout)[keyof typeof FormLayout];

export const isFormLayout = (value: unknown): value is FormLayout =>
  value === FormLayout.HORIZONTAL || value === FormLayout.VERTICAL;

export interface ConditionalControl {
  q_id: string;
  /**
   * Stable, author-set identifier for this branch field, mirroring
   * FormControl.mapping_key. CORE keys its field mapping off this rather than the
   * env-specific q_id, and the backend already emits it for branch controls
   * (flattenJobTaskForm.ts pushAnswer), so a branch answer is only mappable in
   * CORE once one is set here.
   */
  mapping_key?: string;
  control_name: string;
  control_type: string;
  control_description: string;
  control_span_length: string;
  required: boolean;
  /**
   * Author-set flag (CWL-304): the field is omitted from builder preview (and,
   * once mobile honours it, from the rendered question). Still persisted with
   * label, description, mapping_key, and included in the submit payload.
   * Not mutually exclusive with `required` — Hidden is display, not a submit rule.
   */
  hidden?: boolean;
  /**
   * Non-editable on mobile once mobile honours this flag (CWL-305). Mutually
   * exclusive with `required` in the builder UI and in validateFormDefinition —
   * a field the user cannot edit cannot also be one they're blocked for leaving
   * blank.
   */
  read_only?: boolean;
  min: string;
  max: string;
  order: number | null;
  options: ControlOption[];
  conditions: Condition[];
  validations: any[];
  validationmsg: string;
  nestedControls: any[];
  value: string | null;
  data_type: string;
  is_multi_select: boolean;
  s3_bucket_name: string;
  s3_folder_name: string;
}

export interface Condition {
  condition_name: string;
  condition_order: number;
  next_q_id: string | null;
  /**
   * Marks this branch as terminal: answering the conditional question this way
   * finishes the form and goes straight to the summary/save step, even when
   * later questions exist.
   *
   * On the wire this is expressed as `next_q_id: ""` — mobile compares strictly
   * against the empty string to go to the summary, and treats null as "advance to
   * the next section" instead. The flag exists so the *builder* can tell an
   * author's deliberate "end here" apart from an unset target (null), and record
   * the intent for the properties panel.
   */
  end_form?: boolean;
  conditionalControls: ConditionalControl[];
}

export interface FormControl {
  q_id: string;
  /**
   * Stable, author-set identifier for this field, used to map its answer to CORE
   * fields when the form payload is sent on validation. Unlike `q_id` (auto-
   * generated and environment-specific), the mapping key is intentional and stable
   * across environments. Optional and free-text, but must be unique within a form.
   */
  mapping_key?: string;
  control_name: string;
  control_type: string;
  control_description: string;
  control_span_length: string;
  /** Empty on every non-conditional control; a widget type on a conditional_field. */
  conditional_type: ConditionalType | '';
  required: boolean;
  /**
   * Field library flag (CWL-304): applies to every control type. Hides the
   * entire question in builder preview (label + input). Not yet honoured by
   * mobile — this ships the authoring capability only.
   */
  hidden?: boolean;
  /**
   * Field library flag (CWL-305): applies to every control type, letting
   * authors make any field non-editable, typically for a value that is
   * auto-populated elsewhere and should not be changed by the user. Mutually
   * exclusive with `required` in the builder UI and in validateFormDefinition.
   * Not yet honoured by mobile — this ships the authoring capability only.
   */
  read_only?: boolean;
  min: string;
  max: string;
  order: number | null;
  prev_q_id?: string | null;
  next_q_id: string | null;
  /**
   * Marks this question as the last one: the form finishes here and goes to the
   * summary/save step, even though later questions exist in the form.
   *
   * Without this flag a null `next_q_id` is ambiguous — `syncFormControlLinks`
   * cannot tell "end the form here" from "not set yet" and overwrites it with
   * the next question in sequence, making end-of-form inexpressible mid-form.
   * Persisted as `next_q_id: ""`, the sentinel mobile compares against.
   */
  end_form?: boolean;
  options: ControlOption[];
  nested_controls?: any[];
  nestedControls?: any[];
  conditions: Condition[];
  validations: any[];
  value: string | null;
  s3_bucket_name: string;
  s3_folder_name: string;
  calculated_type?: string;
  field1_value?: string;
  field2_value?: string;
  operator?: string;
  help_text?: string;
  is_repeatable?: boolean;
  min_repeat?: number;
  max_repeat?: number;
  data_type?: string;
  is_multi_select?: boolean;
  group_name?: string;
  group_description?: string;
  group_controls?: boolean;
}

export interface FormSection {
  id: number | null;
  rev_num: number | null;
  sequence: number;
  section_name: string;
  section_description: string | null;
  is_table_view: boolean | null;
  is_repeatable?: boolean;
  max_repeat?: number;
  form_id: number | null;
  created_by: string | number;
  created_time: string;
  updated_by: string | number;
  updated_time: string;
  is_active: boolean;
  section_number: number;
  controls: FormControl[];
}

export interface FormDefinition {
  id: number | null;
  name: string;
  description: string;
  image_url: string;
  rev_num: number | null;
  created_by: string | number;
  created_time: string;
  updated_by: string | number;
  updated_time: string;
  is_delete: boolean;
  form_type_id: number | null;
  layout: string;
  is_dynamic: boolean;
  form: boolean;
  is_system: boolean;
  sections: FormSection[];
  compliant?: boolean;
  can_take_action?: boolean;
  is_compliant_submitted?: boolean;
  form_type_name?: string;
}

export interface ValidationIssue {
  path: string;
  message: string;
}

export interface FieldTemplate {
  type: string;
  label: string;
  description?: string;
  category: string;
  defaults?: Partial<FormControl>;
}

export type SelectedTarget = {
  sectionIndex: number;
  controlIndex: number | null;
};

export type PropertiesTarget =
  | { type: 'form' }
  | { type: 'section'; sectionIndex: number }
  | { type: 'control'; sectionIndex: number; controlIndex: number };

export type PendingDelete =
  | { type: 'section'; sectionIndex: number }
  | { type: 'control'; sectionIndex: number; controlIndex: number }
  | null;

export type FormTypeOption = { value: number; label: string };

export interface ControlConfig {
  id: number;
  name: string;
  label: string;
  description?: string;
  option?: unknown;
  validations?: unknown;
}
